export type StormPhaseInfo = {
  phase: number;
  phaseLabel: string;
  etaSeconds: number | null;
};

const PHASE_LABELS: Record<number, string> = {
  1: 'Phase 1 — Wide open',
  2: 'Phase 2 — Closing in',
  3: 'Phase 3 — Storm tightens',
  4: 'Phase 4 — Zone shrinks',
  5: 'Phase 5 — Endgame',
  6: 'Phase 6 — Final circle',
};

function clampPhaseFromProgress(progress: number): number {
  const p = Math.max(0, progress);
  const raw = Math.floor(p * 6) + 1;
  return Math.min(6, Math.max(1, raw));
}

/**
 * Maps storm radius (initial → min) into six phases. Phase 6 is the last band near `minRadius`.
 * `shrinkPerSecond` is treated as positive radius decrease per second for ETA.
 */
export function getStormPhaseInfo(opts: {
  initialRadius: number;
  minRadius: number;
  currentRadius: number;
  shrinkPerSecond: number;
}): StormPhaseInfo {
  const { initialRadius, minRadius, currentRadius, shrinkPerSecond } = opts;
  const range = initialRadius - minRadius;

  if (!Number.isFinite(range) || range <= 0) {
    return { phase: 6, phaseLabel: PHASE_LABELS[6]!, etaSeconds: null };
  }

  const clampedR = Math.max(minRadius, Math.min(initialRadius, currentRadius));
  const progress = (initialRadius - clampedR) / range;
  const phase = clampPhaseFromProgress(progress);
  const phaseLabel = PHASE_LABELS[phase] ?? PHASE_LABELS[6]!;

  let etaSeconds: number | null = null;
  if (phase < 6 && shrinkPerSecond > 0 && Number.isFinite(shrinkPerSecond)) {
    const band = range / 6;
    const nextBoundaryRadius = initialRadius - phase * band;
    const deltaR = currentRadius - nextBoundaryRadius;
    if (deltaR > 0) {
      etaSeconds = deltaR / shrinkPerSecond;
    } else {
      etaSeconds = 0;
    }
  }

  return { phase, phaseLabel, etaSeconds };
}
